import { requireOrdinaryEntityType } from '@einhaus/project314-shared';
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '@/types/appRouter';

type RouterOutputs = inferRouterOutputs<AppRouter>;
type RouterInputs = inferRouterInputs<AppRouter>;

export type ReviewQueue = RouterOutputs['intelReview']['listQueue'];
export type ReviewQueueItem = ReviewQueue['items'][number];
export type PostReview = RouterOutputs['intelReview']['getPost'];
export type ModelCall = NonNullable<PostReview['predictions']>['modelCalls'][number];
export type ReviewScore = RouterOutputs['intelReview']['score'];
export type CallLabelScore = ReviewScore['all'];
export type LabelledCall = RouterInputs['intelReview']['saveLabels']['calls'][number];
export type LabelledTarget = LabelledCall['targets'][number];
export type InstrumentDisplay = PostReview['instruments'][number];

/** A call being edited; `key` keeps React rows stable while calls are added and removed. */
export type DraftCall = LabelledCall & { key: string };

type Choice<T extends string> = { value: T; label: string };

export const statementKindChoices: Choice<LabelledCall['statementKind']>[] = [
    { value: 'recommendation', label: 'Recommendation (tells readers to act)' },
    { value: 'forecast', label: 'Forecast (predicts price or direction)' },
    { value: 'trade_claim', label: 'Trade (says they traded)' },
    { value: 'holding_claim', label: 'Holding (says they hold)' },
    { value: 'retrospective', label: 'Looking back at an earlier call' }
];

export const biasChoices: Choice<'' | NonNullable<LabelledCall['underlyingBias']>>[] = [
    { value: '', label: 'No clear direction' },
    { value: 'bullish', label: 'Bullish' },
    { value: 'bearish', label: 'Bearish' }
];

export const positionSideChoices: Choice<'' | NonNullable<LabelledCall['positionSide']>>[] = [
    { value: '', label: 'Not stated' },
    { value: 'long', label: 'Long (owns it)' },
    { value: 'short', label: 'Short' }
];

export const actionChoices: Choice<LabelledCall['action']>[] = [
    { value: 'none', label: 'None (view only)' },
    { value: 'buy', label: 'Buy' },
    { value: 'add', label: 'Add' },
    { value: 'hold', label: 'Hold' },
    { value: 'trim', label: 'Trim (part)' },
    { value: 'sell', label: 'Sell' },
    { value: 'exit', label: 'Exit (all)' },
    { value: 'short', label: 'Short' },
    { value: 'cover', label: 'Cover' },
    { value: 'avoid', label: 'Avoid' },
    { value: 'watch', label: 'Watch' }
];

export const lifecycleChoices: Choice<LabelledCall['statedLifecycle']>[] = [
    { value: 'none', label: 'No stated change' },
    { value: 'raise_target', label: 'Raises target' },
    { value: 'lower_target', label: 'Lowers target' },
    { value: 'change_stop', label: 'Changes stop' },
    { value: 'close', label: 'Closes the view or position' },
    { value: 'withdraw', label: 'Withdraws the view' },
    { value: 'reverse', label: 'Flips to the opposite view' }
];

export const horizonChoices: Choice<LabelledCall['horizonClass']>[] = [
    { value: 'unspecified', label: 'Not stated' },
    { value: 'intraday', label: 'Intraday' },
    { value: 'days', label: 'Days' },
    { value: 'weeks', label: 'Weeks' },
    { value: 'months', label: 'Months' },
    { value: 'year_plus', label: 'A year or more' }
];

export const formChoices: Choice<LabelledCall['instrumentForm']>[] = [
    { value: 'stock', label: 'Stock or ETF' },
    { value: 'option', label: 'Option' },
    { value: 'crypto', label: 'Crypto' },
    { value: 'index', label: 'Index' }
];

export const optionTypeChoices: Choice<NonNullable<LabelledCall['optionType']>>[] = [
    { value: 'call', label: 'Call' },
    { value: 'put', label: 'Put' }
];

export const targetRoleChoices: Choice<LabelledTarget['targetRole']>[] = [
    { value: 'price_target', label: 'Target' },
    { value: 'entry_zone', label: 'Entry level' },
    { value: 'stop_loss', label: 'Stop' }
];

export const labelOf = <T extends string>(choices: Choice<T>[], value: T | null): string =>
    choices.find((choice) => choice.value === (value ?? ''))?.label ?? String(value ?? '—');

export const instrumentKey = (call: { entityType: string; entityId: number }): string => `${call.entityType}:${call.entityId}`;

let draftSequence = 0;
export const toDraft = (
    call: Omit<LabelledCall, 'entityType'> & { entityType: import('@einhaus/project314-types').EntityType }
): DraftCall => ({
    ...call,
    entityType: requireOrdinaryEntityType(call.entityType),
    targets: call.targets.map((target) => ({ ...target })),
    key: `call-${++draftSequence}`
});

/** Problems that would make the server refuse the labels, as messages per call. */
export const draftProblems = (calls: DraftCall[]): string[] =>
    calls.flatMap((call, index) => {
        const position = `Call ${index + 1}`;
        const problems: string[] = [];
        if ((call.instrumentForm === 'option') !== (call.optionType !== null))
            problems.push(`${position}: choose call or put for an option (and only for an option)`);
        if (call.claimedEntryPrice !== null && !(call.claimedEntryPrice > 0)) problems.push(`${position}: entry price must be positive`);
        if (call.targets.some((target) => !(target.priceLow > 0) || target.priceLow > target.priceHigh)) {
            problems.push(`${position}: every level needs a positive low no higher than its high`);
        }
        return problems;
    });

export const formatEastern = (unix: number): string =>
    new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short', timeZone: 'America/New_York' }).format(unix * 1_000);
